import { useState } from 'react';
import { Plus, Wheat, TrendingUp, TrendingDown, Target, CreditCard as Edit2, Trash2, ChevronDown, ChevronUp, BarChart3, Receipt } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAgriCampaigns, useAllAgriCharges } from '../../hooks/useAgriculture';
import { computeCampaignKPIs, computePriceScenarios, computeYieldScenarios, CROP_TYPE_LABELS, formatEur, formatNum } from '../../utils/agriEngine';
import type { AgriCampaign } from '../../utils/agriEngine';
import { CampaignFormModal } from './CampaignFormModal';
import { ChargesModal } from './ChargesModal';

interface Props { year: number; }

export function CampaignsTab({ year }: Props) {
  const { campaigns, loading, refresh } = useAgriCampaigns(year);
  const { chargesByCampaign, refresh: refreshCharges } = useAllAgriCharges(year);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AgriCampaign | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [chargesCampaign, setChargesCampaign] = useState<AgriCampaign | null>(null);
  const [priceVariation, setPriceVariation] = useState(0);

  async function handleDelete(id: string) {
    if (!confirm('Supprimer cette campagne et toutes ses charges ?')) return;
    await supabase.from('agri_campaigns').delete().eq('id', id);
    refresh();
    refreshCharges();
  }

  function handleSaved() {
    setShowForm(false);
    setEditing(null);
    refresh();
    refreshCharges();
  }

  const toggleExpand = (id: string) => setExpandedId(prev => prev === id ? null : id);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full"></div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Cycles culturaux {year}</h2>
          <p className="text-sm text-gray-500">{campaigns.length} campagne{campaigns.length !== 1 ? 's' : ''} enregistrée{campaigns.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm font-medium shadow-sm">
          <Plus className="w-4 h-4" />
          Nouvelle campagne
        </button>
      </div>

      {campaigns.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Wheat className="w-8 h-8 text-green-400" />
          </div>
          <h3 className="text-gray-700 font-medium mb-2">Aucune campagne pour {year}</h3>
          <p className="text-gray-400 text-sm mb-6">Créez votre première campagne pour piloter vos cycles culturaux</p>
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-medium">
            <Plus className="w-4 h-4" />
            Créer une campagne
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map(c => {
            const charges = chargesByCampaign[c.id] || [];
            const kpi = computeCampaignKPIs(c, charges);
            const isExpanded = expandedId === c.id;
            const scenarios = computePriceScenarios(c, charges);
            const yieldScenarios = computeYieldScenarios(c, charges);
            const currentScenario = scenarios.find(s => s.variation_pct === priceVariation) || scenarios[2];

            return (
              <div key={c.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Wheat className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{c.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            c.status === 'en_cours' ? 'bg-blue-100 text-blue-700' :
                            c.status === 'recolte' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-600'}`}>
                            {c.status === 'en_cours' ? 'En cours' : c.status === 'recolte' ? 'Récolté' : 'Clôturée'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">{CROP_TYPE_LABELS[c.crop_type]} · {formatNum(c.surface_ha, 1)} ha · {formatNum(c.target_yield_qtx_ha)} qtx/ha · {formatEur(c.target_price_eur_t)}/t</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => setChargesCampaign(c)} className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors" title="Gérer les charges">
                        <Receipt className="w-4 h-4" />
                      </button>
                      <button onClick={() => { setEditing(c); setShowForm(true); }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(c.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => toggleExpand(c.id)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                    <div className="bg-blue-50 rounded-xl p-3">
                      <p className="text-xs text-blue-600 font-medium">Production</p>
                      <p className="text-lg font-bold text-blue-900">{formatNum(kpi.production_tonnes)} t</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-3">
                      <p className="text-xs text-green-600 font-medium">Chiffre d'affaires</p>
                      <p className="text-lg font-bold text-green-900">{formatEur(kpi.gross_revenue)}</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-3">
                      <p className="text-xs text-red-600 font-medium">Charges totales</p>
                      <p className="text-lg font-bold text-red-900">{formatEur(kpi.total_charges)}</p>
                      <p className="text-xs text-red-500">{charges.length} poste{charges.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div className={`rounded-xl p-3 ${kpi.gross_margin >= 0 ? 'bg-emerald-50' : 'bg-orange-50'}`}>
                      <p className={`text-xs font-medium ${kpi.gross_margin >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>Marge brute</p>
                      <p className={`text-lg font-bold ${kpi.gross_margin >= 0 ? 'text-emerald-900' : 'text-orange-900'}`}>{formatEur(kpi.gross_margin)}</p>
                      <p className={`text-xs ${kpi.gross_margin >= 0 ? 'text-emerald-500' : 'text-orange-500'}`}>{formatEur(kpi.margin_per_ha)}/ha</p>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 p-5 space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="bg-white rounded-xl p-4 border border-gray-100">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <Target className="w-4 h-4 text-orange-500" />
                          Seuils critiques
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Prix de seuil de rentabilité</span>
                            <span className="font-semibold text-orange-600">{formatEur(kpi.break_even_price)}/t</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Rendement seuil</span>
                            <span className="font-semibold text-orange-600">{formatNum(kpi.break_even_yield, 1)} qtx/ha</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Marge/ha</span>
                            <span className={`font-semibold ${kpi.margin_per_ha >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatEur(kpi.margin_per_ha)}/ha</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-xl p-4 border border-gray-100">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-blue-500" />
                          Sensibilité au prix
                        </h4>
                        <div className="mb-3">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>-20%</span>
                            <span>Variation : {priceVariation > 0 ? '+' : ''}{priceVariation}%</span>
                            <span>+20%</span>
                          </div>
                          <input type="range" min="-20" max="20" step="5" value={priceVariation}
                            onChange={e => setPriceVariation(parseInt(e.target.value))}
                            className="w-full accent-green-600" />
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Prix simulé</span>
                            <span className="font-medium">{formatEur(currentScenario.price)}/t</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Marge simulée</span>
                            <span className={`font-semibold ${currentScenario.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatEur(currentScenario.margin)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Impact vs base</span>
                            <span className={`font-semibold text-sm flex items-center gap-1 ${currentScenario.margin_delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {currentScenario.margin_delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                              {currentScenario.margin_delta >= 0 ? '+' : ''}{formatEur(currentScenario.margin_delta)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-gray-100">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Scénarios climatiques (rendement)</h4>
                      <div className="grid grid-cols-3 gap-3">
                        {yieldScenarios.map(s => (
                          <div key={s.label} className={`rounded-lg p-3 border ${s.impact === 0 ? 'bg-blue-50 border-blue-200' : s.impact < -10000 ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
                            <p className="text-xs font-medium text-gray-600 mb-1">{s.label}</p>
                            <p className="text-sm font-bold text-gray-800">{formatNum(s.yield_qtx_ha, 0)} qtx/ha</p>
                            <p className="text-xs text-gray-500">{formatNum(s.production_tonnes)} t</p>
                            <p className={`text-sm font-semibold mt-1 ${s.margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatEur(s.margin)}</p>
                            {s.impact !== 0 && <p className="text-xs text-red-600">{formatEur(s.impact)}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(showForm || editing) && (
        <CampaignFormModal
          campaign={editing}
          year={year}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}
      {chargesCampaign && (
        <ChargesModal
          campaign={chargesCampaign}
          onClose={() => { setChargesCampaign(null); refreshCharges(); }}
        />
      )}
    </div>
  );
}
